import React from 'react';

interface TextInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  helperText?: string;
  errorText?: string;
  size?: 'sm' | 'md' | 'lg';
}

const sizeClassMap: Record<NonNullable<TextInputProps['size']>, string> = {
  sm: 'h-8 px-2.5 text-xs',
  md: 'h-9 px-3 text-sm',
  lg: 'h-12 px-4 text-base',
};

const TextInput: React.FC<TextInputProps> = ({
  label,
  id,
  className = '',
  helperText,
  errorText,
  size = 'md',
  required,
  ...props
}) => {
  const fieldId = id || props.name;
  const toneClass = errorText
    ? 'border-red-300 dark:border-red-700 focus-visible:ring-red-500'
    : 'border-slate-300 dark:border-slate-700 focus-visible:ring-teal-500';

  const input = (
    <input
      id={fieldId}
      aria-invalid={!!errorText}
      className={`mt-1 w-full rounded-lg border bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 outline-none transition-colors focus-visible:ring-2 focus-visible:ring-offset-0 ${toneClass} ${sizeClassMap[size]} ${className}`}
      {...props}
    />
  );

  if (!label) {
    return (
      <>
        {input}
        {errorText ? <p className="mt-1 text-xs font-semibold text-red-600 dark:text-red-400">{errorText}</p> : null}
        {!errorText && helperText ? <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{helperText}</p> : null}
      </>
    );
  }

  return (
    <label htmlFor={fieldId} className="text-xs text-slate-600 dark:text-slate-300">
      <span className="inline-flex items-center gap-1">
        <span>{label}</span>
        {required ? <span className="text-red-500">*</span> : null}
      </span>
      {input}
      {errorText ? <p className="mt-1 text-xs font-semibold text-red-600 dark:text-red-400">{errorText}</p> : null}
      {!errorText && helperText ? <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{helperText}</p> : null}
    </label>
  );
};

export default TextInput;
